import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SOCIAL } from "@/constants/testIds";
import { Link2, Unlink, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Social() {
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState({ connections: [], ad_accounts: [] });
  const [metaAdAccounts, setMetaAdAccounts] = useState([]);
  const [metaPages, setMetaPages] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const [p, a] = await Promise.all([api.get("/social/platforms"), api.get("/social/accounts")]);
    setPlatforms(p.data); setAccounts(a.data);
  };

  const loadMetaResources = async () => {
    setLoadingMeta(true);
    try {
      const [aa, pg] = await Promise.all([
        api.get("/social/meta/adaccounts").catch(() => ({ data: { ad_accounts: [] } })),
        api.get("/social/meta/pages").catch(() => ({ data: { pages: [] } })),
      ]);
      setMetaAdAccounts(aa.data.ad_accounts || []);
      setMetaPages(pg.data.pages || []);
    } finally { setLoadingMeta(false); }
  };

  useEffect(() => { load(); }, []);

  // Handle ?meta=connected or ?meta=error redirect coming back from OAuth
  useEffect(() => {
    const status = params.get("meta");
    if (status === "connected") {
      toast.success("Meta account connected");
      load();
      // Clean up the URL
      params.delete("meta"); setParams(params, { replace: true });
    } else if (status === "error") {
      toast.error(`Meta connect failed: ${params.get("reason") || "unknown"}`);
      params.delete("meta"); params.delete("reason");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metaConn = accounts.connections.find((c) => c.platform === "meta" && !c.is_mock);

  // Whenever Meta becomes connected, fetch their ad accounts + pages from Meta
  useEffect(() => {
    if (metaConn) loadMetaResources();
    else { setMetaAdAccounts([]); setMetaPages([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaConn?.connected_at]);

  const connectMeta = async () => {
    try {
      const { data } = await api.get("/auth/meta/login");
      window.location.href = data.url;   // full-page redirect to Facebook
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Connect failed: ${detail}`);
    }
  };

  const disconnectMeta = async () => {
    if (!confirm("Disconnect your Meta account? Any in-progress publishes will not be affected.")) return;
    try {
      await api.delete("/social/meta/disconnect");
      toast.success("Meta disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div>
        <h1 className="text-3xl font-display text-gray-900">Channels</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-xl">
          Connect ad accounts to publish directly. Meta is live; other platforms are placeholder adapters.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {platforms.map((p) => {
          const isMeta = p.id === "meta";
          const connected = isMeta && metaConn;
          return (
            <div key={p.id}
              className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg text-gray-900">{p.label}</h3>
                {p.live ? (
                  <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${
                    connected ? "text-emerald-700 bg-emerald-50" : "text-gray-500 bg-gray-100"}`}>
                    {connected ? "Connected" : "Available"}
                  </span>
                ) : (
                  <Lock className="h-4 w-4 text-gray-300" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-2 min-h-[2.5em]">
                {p.live
                  ? (connected
                      ? `Linked as ${metaConn.meta_user_name || "Meta user"}`
                      : "Connect to publish ads & sync performance.")
                  : "Placeholder adapter — available once production credentials are added."}
              </p>
              <div className="mt-4">
                {isMeta && (
                  connected ? (
                    <button data-testid={SOCIAL.disconnectMetaBtn} onClick={disconnectMeta}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-medium transition-colors">
                      <Unlink className="h-3.5 w-3.5" /> Disconnect
                    </button>
                  ) : (
                    <button data-testid={SOCIAL.connectMetaBtn} onClick={connectMeta}
                      className="btn-brand inline-flex items-center gap-2 px-3 py-1.5 text-xs">
                      <Link2 className="h-3.5 w-3.5" /> Connect Meta
                    </button>
                  )
                )}
                {!isMeta && (
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">Coming soon</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {metaConn && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Meta resources {loadingMeta && <RefreshCw className="inline h-3 w-3 ml-1 animate-spin text-gray-400" />}
            </h3>
            <button onClick={loadMetaResources}
              className="text-xs text-gray-500 hover:text-brand font-medium inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                  Ad accounts <span className="text-gray-400">({metaAdAccounts.length})</span>
                </p>
              </div>
              {metaAdAccounts.length === 0 ? (
                <p className="px-5 py-6 text-sm text-gray-500">
                  {loadingMeta ? "Loading…" : "No ad accounts found. Create one in Meta Business Manager."}
                </p>
              ) : metaAdAccounts.map((a) => (
                <div key={a.id} className="px-5 py-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-gray-900">{a.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5 font-mono">{a.id}</p>
                  </div>
                  <span className="text-xs font-medium text-brand">{a.currency}</span>
                </div>
              ))}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                  Facebook Pages <span className="text-gray-400">({metaPages.length})</span>
                </p>
              </div>
              {metaPages.length === 0 ? (
                <p className="px-5 py-6 text-sm text-gray-500">
                  {loadingMeta ? "Loading…" : "No Pages found. You need a Page to attach ads to."}
                </p>
              ) : metaPages.map((p) => (
                <div key={p.id} className="px-5 py-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5 font-mono">{p.id}</p>
                  </div>
                  {p.instagram_business_account_id && (
                    <span className="text-[10px] uppercase tracking-wider text-pink-700 bg-pink-50 px-1.5 py-0.5 rounded">
                      IG linked
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
